import type { ReactElement } from 'react';
import { useState } from 'react';
import type { ChannelSummary } from '@contracts/channels';
import { useChannelActions } from '../api/queries';
import { Button, Checkbox, Modal, useNotices } from '../ui';

/**
 * One meeting into several channels at once (PRD 58).
 *
 * Vladimir, 2026-09-24: *"I need to be able to select several channels in the
 * meeting to add them to"* — the menu posted to one and closed, so three
 * channels were three trips through it. Posting is sharing, so the page says
 * who each one reaches before anything is pressed.
 */
export function PostToChannels({
  meetingId,
  channels,
  onClose,
}: {
  meetingId: string;
  /** The ones it could go to — theirs, and not already holding it. */
  channels: ChannelSummary[];
  onClose: () => void;
}): ReactElement {
  const actions = useChannelActions();
  const { show } = useNotices();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const toggle = (id: string, on: boolean) =>
    setPicked((was) => {
      const next = new Set(was);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const chosen = channels.filter((c) => picked.has(c.id));
  const names = (list: ChannelSummary[]) => list.map((c) => c.name).join(', ');

  const post = async () => {
    setBusy(true);
    const posted: ChannelSummary[] = [];
    const refused: ChannelSummary[] = [];
    // One at a time: each is its own share, and one refusal must not cost
    // the others.
    for (const c of chosen) {
      try {
        await actions.post.mutateAsync({ id: c.id, meetingId });
        posted.push(c);
      } catch {
        refused.push(c);
      }
    }
    setBusy(false);
    if (refused.length === 0) {
      show({
        tone: 'success',
        text: `Added to ${names(posted)} — everybody in ${posted.length === 1 ? 'it' : 'them'} can open it now.`,
      });
      onClose();
      return;
    }
    setPicked(new Set(refused.map((c) => c.id)));
    show({
      tone: 'failure',
      text: `${posted.length ? `Added to ${names(posted)}, but not` : 'Could not add it'} to ${names(refused)} — try again.`,
    });
  };

  return (
    <Modal
      title="Add to collections"
      onClose={onClose}
      foot={
        <>
          <Button variant="quiet" onClick={onClose}>
            Not now
          </Button>
          <Button
            variant="primary"
            disabled={chosen.length === 0}
            working={busy ? 'Adding…' : false}
            onClick={() => void post()}
          >
            {chosen.length === 0
              ? 'Choose collections'
              : chosen.length === 1
                ? `Add to ${chosen[0].name}`
                : `Add to ${chosen.length} collections`}
          </Button>
        </>
      }
    >
      <p className="t-secondary">
        Adding it shares the meeting with everybody in the collection — and with whoever joins it
        later.
      </p>
      <div className="stack" style={{ gap: 10 }}>
        {channels.map((c) => (
          <div key={c.id} className="stack" style={{ gap: 2 }}>
            <Checkbox
              label={c.name}
              checked={picked.has(c.id)}
              onChange={(e) => toggle(c.id, e.target.checked)}
            />
            <span className="why" style={{ marginLeft: 26 }}>
              {c.memberCount} {c.memberCount === 1 ? 'person' : 'people'}
              {c.visibility === 'PRIVATE' ? ' · private' : ' · public'}
            </span>
          </div>
        ))}
      </div>
    </Modal>
  );
}
